import { Location } from '@angular/common';
import { AfterContentInit, AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, QueryList, signal, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { BadgeColor, BadgeKind, BaseViewComponent, ButtonKind, ButtonSize, Enum, FormBase, GetAnimationDuration, Icon, IconSize, Orientation, TabSize } from '@sb/library/base';
import { FormManager, StyleManager } from '@sb/library/managers';
import { fromEvent, Subscription } from 'rxjs';
import { TabComponent } from '../../tab.component';
import { TabGroupBodyComponent } from '../body/tab-group-body.component';

enum TabChangeMode {
    None = 'None',
    Init = 'Init',
    Click = 'Click',
    Route = 'Route',
    Manual = 'Manual',
    AddFragmentToURL = 'AddFragmentToURL',
}

@Component({
    selector: 'lib-tab-group-header',
    templateUrl: './tab-group-header.component.html',
    styleUrls: ['./tab-group-header.component.scss'],
})
export class TabGroupHeaderComponent<T extends Enum<string>> extends BaseViewComponent implements OnInit, AfterContentInit, AfterViewInit {
    @ViewChild('header') header!: MatTabGroup;
    @ViewChild('topElement') topElement!: ElementRef;

    @Input() stickyHeader: boolean = false;
    @Input() type!: Enum<any>;
    @Input() enableFormProtection: boolean = false;
    @Input() isInNavigation: boolean = false;
    @Input() hasBackground: boolean = false;
    @Input() bodyComponent!: TabGroupBodyComponent<T>;
    @Input() size: TabSize = this.isMobile ? TabSize.Small : TabSize.Medium;
    @Input() orientation: Orientation = Orientation.Horizontal;

    private _selectedTabAtInit?: T;
    @Input() set selectedTab(value: T) {
        if (this._tabEnum) {
            this.SetSelectedTab(value);
        } else {
            this._selectedTabAtInit = value;
        }
    }

    @Output() selectedTabChange: EventEmitter<T> = new EventEmitter();
    @Output() tabAnimationDone: EventEmitter<void> = new EventEmitter();

    get animationDuration(): string {
        return `${GetAnimationDuration(this.isMobile || this.isTablet)}ms`;
    }
    
    private _canScrollLeft: boolean = false;
    private _canScrollRight: boolean = false;
    

    private _formByTab: Partial<Record<T, FormGroup<any> | null>> = {};
    private _tabEnum!: T[];
    private _tabChangeMode: TabChangeMode = TabChangeMode.Init;
    private _selectedIndex: number | null = null;
    private _initialRoute!: string | null;
    private _scrollTopBeforeTabChange?: number;
    private _scrollStep: number = 300; // Pixels to scroll on each click

  

    get selectedIndicatorOffset(): string {
        return `${(this.selectedIndex ?? 0) * 100}%`;
    }

    constructor(
        private _StyleManager: StyleManager,
        private _FormManager: FormManager,
        private _Router: Router,
        private _ActivatedRoute: ActivatedRoute,
        private _Location: Location
    ) {
        super();
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this._tabEnum = Object.values(this.type);

        if (this._tabEnum.some(value => typeof value === 'number')) {
            throw Error('Tabs must be defined using string-type enums only');
        }

        if (this.isInNavigation) {
            this._subscriptions.push(this.SubscribeToActivatedRouteFragment());
        }

    }

    ngAfterContentInit(): void {
        if (this.bodyComponent.tabComponentQueryList) { // we need this if because we have drawers that if its only one tab - we dont use tabs at all - should this really be handled by the tab itself instead of the developer
            const tabs = this.bodyComponent.tabComponentQueryList.toArray();
            let hiddenTabs = this._tabEnum.length !==  tabs.length;
            if (hiddenTabs) {
                this._tabEnum = tabs.map(t => t.name!);
            }
            let initialTabIsVisible: boolean = this._initialRoute !== null && this._tabEnum.includes(this._initialRoute! as T);
            const tabsSubscription = this.bodyComponent.tabComponentQueryList.changes.subscribe((tabs: QueryList<TabComponent<T>>) => {
                hiddenTabs = this._tabEnum.length !== tabs.length;
                if (hiddenTabs) {
                    const currentTab = this.currentTab;
                    this._tabEnum = tabs.toArray().map(t => t.name!);
                    this._selectedIndex = this._tabEnum.indexOf(currentTab);
                    if (!initialTabIsVisible) {
                        this.NavigateToInitialTab();
                    }
                }
            });
            this._subscriptions.push(tabsSubscription);
            this.NavigateToInitialTab();
        }
    }

    private NavigateToInitialTab(): void {
        if (this.isInNavigation) {
            // We do the initial route here because we need to figure out hidden tabs
            this.HandleRouteFragment(this._initialRoute);
        } else if (this._selectedTabAtInit !== undefined) {
            this.SetSelectedTab(this._selectedTabAtInit);
        } else {
            this.SelectedIndexChangeEvent(0);
        }
        this.bodyComponent.selectedIndex = this._selectedIndex;
    }

    ngAfterViewInit(): void {
        const topElement = this.topElement.nativeElement;
        let bgColor = this._StyleManager.GetRealBackgroundColor(topElement);
        topElement.style.backgroundColor = bgColor;

        this.SetUpTabClickHandler();
        this.SetUpTabKeypressHandler();

        setTimeout(() => {
            this.ScrollSelectedTabIntoView();
            this.SetUpScrollShadows();
        });
    }

    AnimationDone(): void {
        this.tabAnimationDone.emit();
    }

    GetIconSize(size: TabSize): IconSize {
        switch (size) {
            case TabSize.Small:
                return IconSize.Small;
            case TabSize.Medium:
                return IconSize.Medium;
            case TabSize.Large:
                return IconSize.Large;
            default:
                return IconSize.Small;
        }
    }

    ScrollTabsLeft(): void {
        const container = this.GetTabLabelContainer();
        if (container) {
            container.scrollBy({ left: -this._scrollStep, behavior: 'smooth' });
        }
    }

    ScrollTabsRight(): void {
        const container = this.GetTabLabelContainer();
        if (container) {
            container.scrollBy({ left: this._scrollStep, behavior: 'smooth' });
        }
    }

    private GetTabLabelContainer(): HTMLElement | null {
        return this.header?._elementRef.nativeElement.querySelector('.mat-mdc-tab-label-container');
    }

    private SetSelectedTab(value: T) {
        const index = this._tabEnum.indexOf(value);
        if (index >= 0) {
            this._tabChangeMode = TabChangeMode.Manual;
            this.SelectedIndexChangeEvent(index);
        } else {
            console.error('Selected tab passed in as input does not exist');
        }
    }

    private ScrollSelectedTabIntoView(): void {
        const label = this.header?._tabHeader?._items?.toArray()[this._selectedIndex ?? 0];
        label?.elementRef.nativeElement.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    // The background-attachment CSS shadow trick doesn't reliably clear at the scroll ends, so drive it from actual scroll position instead
    private SetUpScrollShadows(): void {
        const header: HTMLElement | null = this.header?._elementRef.nativeElement.querySelector('.mat-mdc-tab-header');
        const container: HTMLElement | null = this.GetTabLabelContainer();
        const list: HTMLElement | null = this.header?._elementRef.nativeElement.querySelector('.mat-mdc-tab-list');
        if (!header || !container || !list) {
            return;
        }

        const updateShadows = () => {
            const canScrollLeft = container.scrollLeft > 0;
            const canScrollRight = container.scrollLeft + container.clientWidth < container.scrollWidth - 1;
            header.classList.toggle('sb-tab-shadow-left', canScrollLeft);
            header.classList.toggle('sb-tab-shadow-right', canScrollRight);
            this._canScrollLeft = canScrollLeft;
            this._canScrollRight = canScrollRight;
        };

        // Observe the tab list (not the container) since its content width is what actually changes as tabs/fonts finish rendering
        updateShadows();
        const resizeObserver = new ResizeObserver(updateShadows);
        resizeObserver.observe(list);
        this._subscriptions.push(new Subscription(() => resizeObserver.disconnect()));
        this._subscriptions.push(fromEvent(container, 'scroll').subscribe(updateShadows));
        this._subscriptions.push(fromEvent(window, 'resize').subscribe(updateShadows));
    }

    private SetUpTabClickHandler() {
        const handleTabClick = this.header._handleClick.bind(this.header);
        this.header._handleClick = (tab, header, index) => {
            if (this._tabChangeMode === TabChangeMode.None) {
                if (index === this._selectedIndex) {
                    handleTabClick(tab, header, index);
                } else {
                    this._tabChangeMode = TabChangeMode.Click;
                    const form = this.enableFormProtection ? this._formByTab[this.currentTab] : null;
                    this._FormManager.canDeactivate(form).subscribe(result => {
                        if (result) {
                            // Material focuses the tab label, which can scroll the document before the index change fires
                            this._scrollTopBeforeTabChange = document.scrollingElement?.scrollTop;
                            BaseViewComponent.BeginTransitionAnimation();
                            handleTabClick(tab, header, index);
                        } else {
                            this._tabChangeMode = TabChangeMode.None;
                        }
                    });
                }
            }
        };
    }

    private SetUpTabKeypressHandler() {
        const handleKeydown = this.header._tabHeader._handleKeydown.bind(this.header._tabHeader);
        this.header._tabHeader._handleKeydown = keydownEvent => {
            const id = (keydownEvent.target as HTMLDivElement)?.id;
            if (id !== undefined && (keydownEvent.key === 'Enter' || keydownEvent.key === ' ')) {
                keydownEvent.stopPropagation();
                keydownEvent.preventDefault();
                const index = Number(id.substring(id.lastIndexOf('-') + 1));
                if (isNaN(index)) {
                    console.error('Angular ID format has changed; cannot perform form protection on tab header keypress');
                    handleKeydown(keydownEvent);
                } else if (index === this._selectedIndex) {
                    handleKeydown(keydownEvent);
                } else if (this._tabChangeMode === TabChangeMode.None) {
                    this._tabChangeMode = TabChangeMode.Click;
                    const form = this.enableFormProtection ? this._formByTab[this.currentTab] : null;
                    this._FormManager.canDeactivate(form).subscribe(result => {
                        if (result) {
                            this._scrollTopBeforeTabChange = document.scrollingElement?.scrollTop;
                            BaseViewComponent.BeginTransitionAnimation();
                            handleKeydown(keydownEvent);
                        } else {
                            this._tabChangeMode = TabChangeMode.None;
                        }
                    });
                }
            } else {
                handleKeydown(keydownEvent);
            }
        };
    }

    SelectedIndexChangeEvent(index: number) {
        this._selectedIndex = index;
        this.bodyComponent.selectedIndex = index;
        this.selectedTabChange.emit(this._tabEnum[this._selectedIndex]);
        this.ScrollSelectedTabIntoView();

        if (this._tabChangeMode === TabChangeMode.AddFragmentToURL) {
            this._tabChangeMode = TabChangeMode.None;
        } else if (this._tabChangeMode !== TabChangeMode.Route) {
            if (this.isInNavigation) {
                const scrollTop = this._scrollTopBeforeTabChange ?? document.scrollingElement?.scrollTop;
                this._scrollTopBeforeTabChange = undefined;
                this._Router.navigate([], { queryParamsHandling: 'preserve', fragment: this._tabEnum[this._selectedIndex] }).then(() => this.ResetScroll(scrollTop));
            }

            if (this.enableFormProtection) {
                // Without setTimeout, sometimes the form is not ready on time (specifically, when the route is entered manually, or back/forward are pressed).
                setTimeout(() => this.HandleFormChange());
            } else {
                this.HandleFormChange();
            }
        }
    }


    GetTabForm(tab: TabComponent<T>) {
        return tab.formOverride ? tab.formOverride : this._formByTab[tab.name];
    }

    // The app shell scrolls the document, not a container element
    private ResetScroll(scrollTop?: number, iteration: number = 5) {
        if (scrollTop === undefined || iteration <= 0) {
            return;
        }

        const element = document.scrollingElement;
        if (element) {
            element.scrollTop = scrollTop;
        }

        requestAnimationFrame(() => this.ResetScroll(scrollTop, iteration - 1));
    }

    private SubscribeToActivatedRouteFragment(): Subscription {
        return this._ActivatedRoute.fragment.subscribe((value: string | null) => {
            if (this._tabChangeMode === TabChangeMode.None) {
                this._tabChangeMode = TabChangeMode.Route;

                const form = this.enableFormProtection ? this._formByTab[this.currentTab] : null;

                this._FormManager.canDeactivate(form).subscribe(result => {
                    if (result) {
                        this.HandleRouteFragment(value);
                    } else {
                        history.pushState(null, document.title, window.location.href);
                        this._tabChangeMode = TabChangeMode.None;
                    }
                });
            } else if (this._tabChangeMode === TabChangeMode.Init) {
                this._initialRoute = value;
            }
        });
    }

    private HandleRouteFragment(value: string | null) {
        if (value && value in this.type) {
            this._selectedIndex = this._tabEnum.indexOf(value as T);
        } else if (value?.includes('?') && value.split('?')[0] in this.type) {
            this._selectedIndex = this._tabEnum.indexOf(value.split('?')[0] as T);
        } else if (value?.includes('&') && value.split('&')[0] in this.type) {
            this._selectedIndex = this._tabEnum.indexOf(value.split('&')[0] as T);
        } else {
            this._selectedIndex = 0;
            const urlTree = this._Router.createUrlTree([], { queryParamsHandling: 'preserve', fragment: this._tabEnum[0] });
            this._tabChangeMode = TabChangeMode.AddFragmentToURL;
            this._Location.replaceState(urlTree.toString()); // replace current history item
        }

        this.bodyComponent.selectedIndex = this._selectedIndex;

        if (this.enableFormProtection) {
            // Without setTimeout, sometimes the form is not ready on time (specifically, when the route is entered manually, or back/forward are pressed).
            setTimeout(() => this.HandleFormChange());
        } else {
            this.HandleFormChange();
        }
    }

    private HandleFormChange() {
        if (this.enableFormProtection) {
            const currentTab = this.currentTab;
            const currentTabForm = this._formByTab[currentTab];

            if (currentTabForm) {
                // Update the current form to the one for this tab
                FormBase.currentForm = currentTabForm;
            } else if (FormBase.currentForm && !Object.values(this._formByTab).some(form => form === FormBase.currentForm)) {
                // Assign the last initialized form to this tab
                const form = FormBase.currentForm;
                this._formByTab[currentTab] = form;
            } else {
                // Otherwise, this tab does not contain a form
                FormBase.currentForm = null;
            }
        }

        if (this.enableFormProtection) {
            setTimeout(() => this._tabChangeMode = TabChangeMode.None, 200);
        } else {
            this._tabChangeMode = TabChangeMode.None;
        }
    }

    get selectedIndex(): number | null {
        return this._selectedIndex;
    }

    get currentTab(): T {
        return this._tabEnum[this._selectedIndex!];
    }

    get canScrollLeft(): boolean {
        return this._canScrollLeft;
    }

    get canScrollRight(): boolean {
        return this._canScrollRight;
    }
}
